import React, { useState } from 'react';
import { MenuSuffixType } from '../../Enums/MenuSuffixType.enum';
import Loading, { LoadingSize } from '../../../../Components/Loading';
import { useAppContext } from '../../../../Services/AppContext';
import {
	LaboratoryResultMiseStatus,
	QuestionnaireResultResult,
	useLazyCasesAssignedCountQuery,
	useLazyLaboratoryResultsMiseCountQuery,
	useLazyNurseCasesCountQuery,
	useLazyQuestionnaireResultsCountQuery,
	useLazyWorkplaceCasesCountQuery,
	useLazyWorkplacePatientDocumentationFilesCountQuery,
} from '../../../../graphql/graphql';
import {
	useLazyGetNewReceivedPackagesCountQuery,
	useLazyGetNewReceivedServiceRequestsCountQuery,
} from '../../../Mzcr/Services/mzcrApi';
import { useEffectAsync } from '../../../../helpers';
import styles from './NavigationMenuSuffix.module.scss';
import { MenuSuffixTheme } from '../../Enums/MenuSuffixTheme.enum';
import { joinClasses } from '../../../../Utils/className.util';

export interface INavigationMenuSuffix {
	type: MenuSuffixType;
	theme?: MenuSuffixTheme;
}

interface Props {
	item: INavigationMenuSuffix;
}

const NavigationMenuSuffix: React.FC<Props> = ({ item }) => {
	const { workplaceId, userData } = useAppContext();
	const [number, setNumber] = useState<number | undefined>(undefined);
	const [isLoading, setIsLoading] = useState(false);
	const [getUploadedFilesFromPatientsCount] =
		useLazyWorkplacePatientDocumentationFilesCountQuery();
	const [getAssignedCasesCount] = useLazyCasesAssignedCountQuery();
	const [getWorkplaceCasesCount] = useLazyWorkplaceCasesCountQuery();
	const [getQuestionnaireResultsCount] =
		useLazyQuestionnaireResultsCountQuery();
	const [getLaboratoryResultsMiseCount] =
		useLazyLaboratoryResultsMiseCountQuery();
	const [getNurseCasesCount] = useLazyNurseCasesCountQuery();
	const [getMzcrReceivedPackagesCount] =
		useLazyGetNewReceivedPackagesCountQuery();
	const [getMzcrReceivedServiceRequestsCount] =
		useLazyGetNewReceivedServiceRequestsCountQuery();

	const typeToRequestMap: Record<
		MenuSuffixType,
		() => Promise<number | undefined>
	> = {
		[MenuSuffixType.UPLOADED_FILES_FROM_PATIENTS]: async () => {
			const res = await getUploadedFilesFromPatientsCount({ workplaceId });
			return (
				res?.data?.workplacePatientDocumentationFiles?.meta?.count ?? undefined
			);
		},
		[MenuSuffixType.ASSIGNED_CASES]: async () => {
			const res = await getAssignedCasesCount();
			return res?.data?.casesAssigned?.meta?.count ?? undefined;
		},
		[MenuSuffixType.WORKPLACE_CASES]: async () => {
			const res = await getWorkplaceCasesCount();
			return res?.data?.workplaceCases?.meta?.count ?? undefined;
		},
		[MenuSuffixType.SIGNIFICANT_RISK_OF_SUICIDE]: async () => {
			const res = await getQuestionnaireResultsCount({
				type: QuestionnaireResultResult.SignificantRiskOfSuicide,
				seen: false,
			});
			return res?.data?.questionnaireResults?.meta?.count ?? undefined;
		},
		[MenuSuffixType.SIGNIFICANT_RISK_OF_MENTAL_DECOMPENSATION]: async () => {
			const res = await getQuestionnaireResultsCount({
				type: QuestionnaireResultResult.SignificantRiskOfMentalDecompensation,
				seen: false,
			});
			return res?.data?.questionnaireResults?.meta?.count ?? undefined;
		},
		[MenuSuffixType.LATAM_DIABETES_ENTRY_QUESTIONNAIRE]: async () => {
			const res = await getQuestionnaireResultsCount({
				questionnaireCodes: [
					'latamDiabetesEntryQuestionnaire',
					'meddiBabyLatamEntry',
				],
				doctorId: userData.id,
			});
			return res?.data?.questionnaireResults?.meta?.count ?? undefined;
		},
		[MenuSuffixType.ALL_UNSEEN_QUESTIONNAIRE]: async () => {
			const res = await getQuestionnaireResultsCount({
				seen: false,
				doctorId: userData.id,
				excludeQuestionnaireCodes: [
					'latamDiabetesEntryQuestionnaireDoctor',
					'meddiBabyLatamEntry',
					'latamDiabetesEntryQuestionnaire',
					'meddiBabyLatamEntryDoctor',
				],
			});
			return res?.data?.questionnaireResults?.meta?.count ?? undefined;
		},
		[MenuSuffixType.LABORATORY_RESULTS_MISE]: async () => {
			const res = await getLaboratoryResultsMiseCount({
				where: {
					status: LaboratoryResultMiseStatus.New,
				},
			});
			return res?.data?.laboratoryResultsMise?.meta?.count ?? undefined;
		},
		[MenuSuffixType.MZCR_RECEIVED_PACKAGES]: async () => {
			const res = await getMzcrReceivedPackagesCount();
			return res?.data ?? undefined;
		},
		[MenuSuffixType.MZCR_RECEIVED_SERVICE_REQUESTS]: async () => {
			const res = await getMzcrReceivedServiceRequestsCount();
			return res?.data ?? undefined;
		},
		[MenuSuffixType.NURSE_CASES]: async () => {
			const res = await getNurseCasesCount();
			return res?.data?.nurseCases?.meta?.count ?? undefined;
		},
	};

	useEffectAsync(async () => {
		setIsLoading(true);
		const fetchFn = typeToRequestMap[item.type];
		if (fetchFn) {
			setNumber(await fetchFn());
		}
		setIsLoading(false);
	}, [item]);

	if (isLoading) {
		return <Loading size={LoadingSize.SMALL} />;
	}

	if (number) {
		return (
			<div
				className={joinClasses([
					styles.wrapper,
					item.theme ? styles[item.theme] : '',
				])}>
				{number}
			</div>
		);
	}

	return <></>;
};

export default NavigationMenuSuffix;
